import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Tree } from 'syngenta-digital-cropwise-react-ui-kit';
import { CropGroup, Farm, Field, PropertySelectorV2Props } from './types';
import { GROUP_BY_CROP, META_COLOR } from './constants';
import {
  collectExpandKeys,
  collectExpandKeysCropMode,
  getCropGroupCropZoneIds,
  getFarmCropZoneIds,
  getFieldCropZoneIds
} from './helpers';
import {
  adjustMeasuredDistances,
  computeConfirmedHidden,
  computeDesiredState,
  findScrollableAncestor,
  hasStructuralChange,
  measureStickyRows,
  scrollFieldNodeToTop
} from './stickyHelpers';
import { CropGroupTitleItem, SelectAllRow } from './titleItems';
import buildFarmNode from './treeNodes';
import './index.css';

// Re-export the extracted pure helpers/types so existing `from './index'`
// imports (unit tests, future consumers) keep resolving unchanged.
export * from './types';
export * from './helpers';
export * from './stickyHelpers';

const PropertySelectorV2: React.FC<PropertySelectorV2Props> = ({
  properties,
  cropZonesChecked,
  setCropZonesChecked,
  groupBy = 'farm',
  onSelectAll,
  geoJSONCollection,
  searchTerm = '',
  scrollToField = null,
  searchCountMap = {},
  fieldCzCountMap = {}
}) => {
  const isCropGroupMode = groupBy === GROUP_BY_CROP;
  const searchActive = searchTerm.length > 0;

  const geometryMap = useMemo(() => {
    const map = new Map<string, any>();
    (geoJSONCollection?.features ?? []).forEach((f: any) => {
      if (f?.properties?.id && f?.geometry) {
        map.set(f.properties.id, f.geometry);
      }
    });
    return map;
  }, [geoJSONCollection]);

  // Derive all crop zone IDs for select-all
  const allCropZoneIds = useMemo(() => {
    if (isCropGroupMode) {
      return (properties as CropGroup[]).flatMap(cg =>
        getCropGroupCropZoneIds(cg)
      );
    }
    return (properties as Farm[]).flatMap(farm => getFarmCropZoneIds(farm));
  }, [properties, isCropGroupMode]);

  // The tree opens fully collapsed (top-level rows only). Selecting a field
  // from the map expands just that branch on demand (see the scrollToField
  // effect); an active search expands matching branches separately.
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  // AC3: expand nodes that contain matching results when search is active.
  // Rule: only expand a node if there is relevant content *below* it that matched.
  //  - Farm always expands (it contains matching fields).
  //  - Field expands only when the match is at cropzone level, NOT when the farm
  //    name or the field name itself is what matched (nothing deeper to reveal).
  useEffect(() => {
    if (!searchActive) return;
    const criteria = searchTerm.toLowerCase();
    const farmKeys: string[] = [];
    const fieldKeys: string[] = [];

    if (isCropGroupMode) {
      (properties as CropGroup[]).forEach(cg =>
        collectExpandKeysCropMode(cg, criteria, farmKeys, fieldKeys)
      );
    } else {
      collectExpandKeys(properties as Farm[], criteria, farmKeys, fieldKeys);
    }

    setExpandedKeys([...farmKeys, ...fieldKeys]);
  }, [searchTerm, properties]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handlers
  const handleSelectAll = useCallback(
    (ids: string[]) => {
      // Always delegate to parent's onSelectAll when provided so it can
      // wrap the heavy state update with its loading-overlay machinery.
      // The parent merges with prev — for non-search `ids` is everything so
      // merge == replace, for search it preserves hidden selections.
      if (onSelectAll) {
        onSelectAll(ids);
      } else if (searchActive) {
        setCropZonesChecked((prev: string[]) => [
          ...new Set([...prev, ...ids])
        ]);
      } else {
        setCropZonesChecked(ids);
      }
    },
    [searchActive, onSelectAll, setCropZonesChecked]
  );

  const handleDeselectAll = useCallback(() => {
    if (searchActive) {
      // AC7: only remove IDs that are currently visible (in filtered properties)
      const visibleIds = new Set(allCropZoneIds);
      setCropZonesChecked((prev: string[]) =>
        prev.filter(id => !visibleIds.has(id))
      );
    } else {
      setCropZonesChecked([]);
    }
  }, [searchActive, allCropZoneIds, setCropZonesChecked]);

  const handleToggleCropZone = useCallback(
    (id: string) => {
      setCropZonesChecked((prev: string[]) =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      );
    },
    [setCropZonesChecked]
  );

  const handleToggleField = useCallback(
    (field: Field) => {
      const fieldIds = getFieldCropZoneIds(field);
      setCropZonesChecked((prev: string[]) => {
        const allChecked = fieldIds.every(id => prev.includes(id));
        if (allChecked) return prev.filter(id => !fieldIds.includes(id));
        return [...new Set([...prev, ...fieldIds])];
      });
    },
    [setCropZonesChecked]
  );

  const handleToggleFarm = useCallback(
    (farm: Farm) => {
      const farmIds = getFarmCropZoneIds(farm);
      setCropZonesChecked((prev: string[]) => {
        const allChecked = farmIds.every(id => prev.includes(id));
        if (allChecked) return prev.filter(id => !farmIds.includes(id));
        return [...new Set([...prev, ...farmIds])];
      });
    },
    [setCropZonesChecked]
  );

  const handleToggleCropGroup = useCallback(
    (cg: CropGroup) => {
      const cgIds = getCropGroupCropZoneIds(cg);
      setCropZonesChecked((prev: string[]) => {
        const allChecked = cgIds.every(id => prev.includes(id));
        if (allChecked) return prev.filter(id => !cgIds.includes(id));
        return [...new Set([...prev, ...cgIds])];
      });
    },
    [setCropZonesChecked]
  );

  const handleExpandKey = useCallback((key: string) => {
    setExpandedKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }, []);

  const treeContainerRef = useRef<HTMLDivElement>(null);

  // When a field is selected from the map: expand its farm (and crop group if
  // applicable) so the field row mounts, then scroll it just below the pinned
  // headers. We intentionally do NOT expand the field itself — its collapsed/
  // expanded state is left as the user had it, so a collapsed field shows only
  // the field and an expanded one shows its crop zones.
  useEffect(() => {
    if (!scrollToField) return undefined;
    const { farmId, fieldId } = scrollToField;

    const cropGroupId = isCropGroupMode
      ? scrollToField.cropId ??
        (properties as CropGroup[]).find(g =>
          g.farms.some(f => f.id === farmId)
        )?.id
      : undefined;

    const farmKey = cropGroupId ? `${cropGroupId}:${farmId}` : farmId;

    setExpandedKeys(prev => {
      const next = new Set(prev);
      next.add(farmKey);
      if (cropGroupId) next.add(cropGroupId);
      return [...next];
    });

    // Expanding a collapsed branch mounts the field row asynchronously, so poll
    // each frame until the branch has *fully* opened, then scroll once; cancel
    // if scrollToField changes mid-wait. Bounded so it can never spin forever.
    //
    // "Fully opened" is two steps: the field row exists AND the dynamic-top
    // effect has applied its sticky offset (written as an inline `top`). On a
    // first collapsed-farm expand the row mounts a frame before that offset is
    // set, and scrolling in that gap lands the field behind the farm header.
    // Waiting for the inline `top` fixes it without re-deriving the offset.
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // ~0.5s at 60fps before giving up
    const tryScroll = () => {
      if (cancelled) return;
      const container = treeContainerRef.current;
      if (container) {
        const fieldEl = container.querySelector(
          `[data-field-id="${CSS.escape(fieldId)}"]`
        ) as HTMLElement | null;
        const treenode = fieldEl?.closest(
          '.syt-antd-tree-treenode'
        ) as HTMLElement | null;
        if (treenode && treenode.style.top !== '') {
          scrollFieldNodeToTop(container, fieldId);
          return;
        }
      }
      if (attempts < MAX_ATTEMPTS) {
        attempts += 1;
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);

    return () => {
      cancelled = true;
    };
  }, [scrollToField]); // eslint-disable-line react-hooks/exhaustive-deps

  // Make expand/collapse arrows keyboard-focusable (Ant Design renders them as
  // non-focusable <span>s). MutationObserver re-patches after every DOM update.
  useEffect(() => {
    const container = treeContainerRef.current;
    if (!container) return undefined;

    const patchSwitchers = () => {
      container
        .querySelectorAll<HTMLElement>(
          '.syt-antd-tree-switcher_open, .syt-antd-tree-switcher_close'
        )
        .forEach(el => {
          if (el.getAttribute('tabindex') === '0') return;
          el.setAttribute('tabindex', '0');
          el.setAttribute('role', 'button');
          el.setAttribute('aria-label', 'Toggle');
        });
    };

    patchSwitchers();

    const observer = new MutationObserver(patchSwitchers);
    observer.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Field sticky-top is hardcoded in CSS at 54px (one farm row), but with
  // wrapping the actual farm row can grow taller. Walk the tree in DOM
  // order and override `top` on each sticky row with the *measured* height
  // of the row(s) it stacks under. ResizeObserver re-runs the pass when
  // any row's height changes (wrap, expand, etc.); MutationObserver picks
  // up rows added or removed from the DOM.
  useEffect(() => {
    const container = treeContainerRef.current;
    if (!container) return undefined;

    // Two-phase to avoid layout thrash: read all heights first (one
    // forced layout), then write all `top` values. Interleaving reads
    // and writes turns this into N layouts and locks the UI on big
    // updates like "select all".
    const apply = () => {
      const rows = Array.from(
        container.querySelectorAll<HTMLElement>(
          '.spv2-crop-node, .spv2-farm-node, .spv2-field-node'
        )
      );
      const heights = rows.map(row => row.offsetHeight);
      let cropGroupHeight = 0;
      let farmHeight = 0;
      rows.forEach((row, i) => {
        /* eslint-disable no-param-reassign */
        if (row.classList.contains('spv2-crop-node')) {
          row.style.top = '0px';
          cropGroupHeight = heights[i];
          farmHeight = 0;
        } else if (row.classList.contains('spv2-farm-node')) {
          row.style.top = `${cropGroupHeight}px`;
          farmHeight = heights[i];
        } else {
          row.style.top = `${cropGroupHeight + farmHeight}px`;
        }
        /* eslint-enable no-param-reassign */
      });
    };

    let applyFrame = 0;
    const scheduleApply = () => {
      if (applyFrame) return;
      applyFrame = requestAnimationFrame(() => {
        applyFrame = 0;
        apply();
      });
    };

    apply();

    const resize = new ResizeObserver(scheduleApply);
    const observeStickyRows = () => {
      container
        .querySelectorAll<HTMLElement>('.spv2-crop-node, .spv2-farm-node')
        .forEach(el => resize.observe(el));
    };
    observeStickyRows();

    // Filter: only react when an actual tree node is added/removed.
    // During select-all antd patches inner DOM (checkbox marks, etc.)
    // many times per frame — those mutations don't affect sticky row
    // positions. Heights are still tracked by ResizeObserver.
    let mutateFrame = 0;
    const mutate = new MutationObserver(mutations => {
      if (!hasStructuralChange(mutations)) return;
      if (mutateFrame) return;
      mutateFrame = requestAnimationFrame(() => {
        mutateFrame = 0;
        resize.disconnect();
        observeStickyRows();
        apply();
      });
    });
    mutate.observe(container, { subtree: true, childList: true });

    return () => {
      resize.disconnect();
      mutate.disconnect();
      if (applyFrame) cancelAnimationFrame(applyFrame);
      if (mutateFrame) cancelAnimationFrame(mutateFrame);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Two sticky rows at the same top coexist whenever the tree (their
  // containing block) is still in view. If farm A wraps to 70px and the
  // next farm B is 50px, A's wrapped bottom (50-70) peeks below B once B
  // becomes the active sticky. Same issue for fields/crop groups. Fix: on
  // scroll, push every earlier-pinned row of a tier up by the height
  // difference vs the latest pinned row, so its visible bottom never
  // extends past the active row above it.
  useEffect(() => {
    const container = treeContainerRef.current;
    if (!container) return undefined;

    const scrollContainer = findScrollableAncestor(container);
    if (!scrollContainer) return undefined;

    const TIERS = ['spv2-crop-node', 'spv2-farm-node', 'spv2-field-node'];
    // What we wrote last frame, so we can (a) recover true positions from a
    // translated row without clearing it first, and (b) only touch the rows
    // whose state actually changes — keeping the scroll frame light so fast
    // flings don't drop frames (the source of the momentary glitches).
    let appliedTranslate = new Map<HTMLElement, number>();
    let hiddenRows = new Set<HTMLElement>();
    let frame = 0;

    // Fast-scroll safeguards. Corrections are written one frame behind the
    // compositor, and during rapid upward scrolls that lag shows: a row still
    // hidden (or translated) from the previous frame when it should already be
    // the visible active header. Two mitigations, neither changing the settled
    // sticky state:
    //   - hide-side hysteresis (computeConfirmedHidden): hides land only after
    //     HIDE_AFTER_FRAMES consecutive stale frames; re-shows are instant.
    //   - fling fallback: when one frame jumps further than FLING_PX_PER_FRAME,
    //     last frame's corrections can only be wrong — drop them all and let
    //     native position:sticky ride the fling out; settle frames re-engage.
    const HIDE_AFTER_FRAMES = 2;
    const FLING_PX_PER_FRAME = 100;
    const staleStreak = new Map<HTMLElement, number>();
    let lastScrollTop = scrollContainer.scrollTop;

    const clearCorrections = () => {
      /* eslint-disable no-param-reassign */
      appliedTranslate.forEach((_offset, row) => {
        row.style.transform = '';
      });
      hiddenRows.forEach(row => {
        row.style.visibility = '';
      });
      /* eslint-enable no-param-reassign */
      appliedTranslate = new Map();
      hiddenRows = new Set();
      staleStreak.clear();
    };

    const refresh = () => {
      frame = 0;

      const { scrollTop } = scrollContainer;
      const flung = Math.abs(scrollTop - lastScrollTop) > FLING_PX_PER_FRAME;
      lastScrollTop = scrollTop;
      if (flung) {
        clearCorrections();
        return;
      }

      // Read (no clear-all first): a translated row's measured distance is its
      // true sticky position minus the px we pushed it last frame, so add that
      // back to recover the true position.
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const measurementsByTier = TIERS.map(tier => {
        const rows = Array.from(
          container.querySelectorAll<HTMLElement>(`.${tier}`)
        );
        return adjustMeasuredDistances(
          measureStickyRows(rows, containerTop),
          appliedTranslate
        );
      });

      // Desired state for this frame. Hides go through the streak ledger so
      // they only land after HIDE_AFTER_FRAMES consecutive stale frames;
      // translates and re-shows apply immediately.
      const {
        translate: nextTranslate,
        hidden: desiredHidden
      } = computeDesiredState(measurementsByTier);
      const nextHidden = computeConfirmedHidden(
        desiredHidden,
        staleStreak,
        HIDE_AFTER_FRAMES
      );

      // Write only the diffs — clear rows leaving each set, set rows entering it.
      /* eslint-disable no-param-reassign */
      appliedTranslate.forEach((_offset, row) => {
        if (!nextTranslate.has(row)) row.style.transform = '';
      });
      nextTranslate.forEach((offsetPx, row) => {
        const value = `translateY(-${offsetPx}px)`;
        if (row.style.transform !== value) row.style.transform = value;
      });
      hiddenRows.forEach(row => {
        if (!nextHidden.has(row)) row.style.visibility = '';
      });
      nextHidden.forEach(row => {
        if (row.style.visibility !== 'hidden') row.style.visibility = 'hidden';
      });
      /* eslint-enable no-param-reassign */

      appliedTranslate = nextTranslate;
      hiddenRows = nextHidden;

      // An unconfirmed hide (streak below the threshold) must self-resolve
      // even if no further scroll event arrives — run one more frame so the
      // streak can complete after scrolling stops.
      let pendingHide = false;
      staleStreak.forEach(streak => {
        if (streak < HIDE_AFTER_FRAMES) pendingHide = true;
      });
      if (pendingHide && !frame) frame = requestAnimationFrame(refresh);
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(refresh);
    };

    scrollContainer.addEventListener('scroll', schedule, { passive: true });

    // Row heights change on wrap reflow (drawer/window resize) and the tree
    // shape changes on expand/collapse — both invalidate the translates set
    // by the last scroll frame. ResizeObserver covers the first, the inner
    // MutationObserver the second.
    const resize = new ResizeObserver(schedule);
    const observeStickyRows = () => {
      container
        .querySelectorAll<HTMLElement>(
          '.spv2-crop-node, .spv2-farm-node, .spv2-field-node'
        )
        .forEach(el => resize.observe(el));
    };
    observeStickyRows();
    // See offset useEffect — same filter rationale: ignore non-structural
    // mutations like checkbox state during select-all.
    const mutate = new MutationObserver(mutations => {
      if (!hasStructuralChange(mutations)) return;
      resize.disconnect();
      observeStickyRows();
      schedule();
    });
    mutate.observe(container, { subtree: true, childList: true });

    refresh();

    return () => {
      scrollContainer.removeEventListener('scroll', schedule);
      resize.disconnect();
      mutate.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTreeKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const target = e.target as HTMLElement;
      if (
        target.classList.contains('syt-antd-tree-switcher_open') ||
        target.classList.contains('syt-antd-tree-switcher_close')
      ) {
        e.preventDefault();
        target.click();
      }
    },
    []
  );

  // Build treeData
  const treeData = useMemo(() => {
    const checkedSet = new Set(cropZonesChecked);
    if (isCropGroupMode) {
      return (properties as CropGroup[]).map(cg => {
        const cgCzIds = getCropGroupCropZoneIds(cg);
        const cgCheckedCount = cgCzIds.filter(id => checkedSet.has(id)).length;
        const isCgChecked =
          cgCzIds.length > 0 && cgCheckedCount === cgCzIds.length;
        const isCgIndeterminate =
          cgCheckedCount > 0 && cgCheckedCount < cgCzIds.length;
        return {
          key: cg.id,
          className: `spv2-crop-node${
            isCgChecked ? ' spv2-node--selected' : ''
          }`,
          title: (
            <CropGroupTitleItem
              cropGroup={cg}
              nodeKey={cg.id}
              isChecked={isCgChecked}
              isIndeterminate={isCgIndeterminate}
              onSelect={handleToggleCropGroup}
              onExpand={handleExpandKey}
              searchTerm={searchTerm}
            />
          ),
          children: cg.farms.map(farm =>
            buildFarmNode(
              farm,
              checkedSet,
              handleToggleFarm,
              handleToggleField,
              handleToggleCropZone,
              handleExpandKey,
              {
                geometryMap,
                searchTerm,
                searchCountMap,
                fieldCzCountMap,
                keyPrefix: cg.id,
                isCropGroupMode
              }
            )
          )
        };
      });
    }
    return (properties as Farm[]).map(farm =>
      buildFarmNode(
        farm,
        checkedSet,
        handleToggleFarm,
        handleToggleField,
        handleToggleCropZone,
        handleExpandKey,
        { geometryMap, searchTerm, searchCountMap, fieldCzCountMap }
      )
    );
  }, [
    properties,
    cropZonesChecked,
    isCropGroupMode,
    geometryMap,
    searchTerm,
    searchCountMap,
    fieldCzCountMap,
    handleToggleFarm,
    handleToggleField,
    handleToggleCropZone,
    handleToggleCropGroup,
    handleExpandKey
  ]);

  if (!properties?.length) {
    return (
      <div
        className="flex items-center justify-center text-sm py-8"
        style={{ color: META_COLOR }}
      >
        No properties available
      </div>
    );
  }

  return (
    <div
      data-testid="property-selector-v2"
      className={`spv2${isCropGroupMode ? ' spv2--crop-mode' : ''}`}
      role="none"
      ref={treeContainerRef}
      onKeyDown={handleTreeKeyDown}
    >
      <SelectAllRow
        allIds={allCropZoneIds}
        checkedIds={cropZonesChecked}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
        searchActive={searchActive}
      />
      <Tree
        treeData={treeData}
        showIcon={false}
        expandedKeys={expandedKeys}
        onExpand={keys => setExpandedKeys(keys as string[])}
        selectable={false}
      />
    </div>
  );
};

export default PropertySelectorV2;
